<script setup lang="ts">
import { usePage } from '@inertiajs/vue3';
import axios from 'axios';
import {
    computed,
    nextTick,
    onMounted,
    onUnmounted,
    ref,
    watch,
    watchEffect,
} from 'vue';
import ToolPageHead from '@/components/seo/ToolPageHead.vue';
import DropZone from '@/components/Tools/DropZone.vue';
import FileCard from '@/components/Tools/FileCard.vue';
import PdfLivePreview from '@/components/Tools/PdfLivePreview.vue';
import StatusMessage from '@/components/Tools/StatusMessage.vue';
import ToolHowToCard from '@/components/Tools/ToolHowToCard.vue';
import ToolLayout from '@/components/Tools/ToolLayout.vue';
import ToolSliderField from '@/components/Tools/ToolSliderField.vue';
import { Button } from '@/components/ui/button';
import { usePdfPage } from '@/composables/usePdfPage';
import { usePdfUploadLimit } from '@/composables/usePdfUploadLimit';
import { useToolPageSeoFromInertia } from '@/composables/useToolPageSeo';
import { useTranslations } from '@/composables/useTranslations';
import { dashboard } from '@/routes';
import { sign as signPage } from '@/routes/tools/pdf';
import signTool from '@/routes/tools/pdf/sign';

const { lineWithMax } = usePdfUploadLimit();
const { t } = useTranslations();
const inertiaPage = usePage();

const seo = useToolPageSeoFromInertia({
    seoPrefix: 'tools.pdf.sign.seo',
    fallbackTitleKey: 'tools.pdf.sign.title',
    fallbackTitleLabel: 'Sign PDF',
    t,
    homeRelativeUrl: dashboard.url(),
    selfRelativeUrl: signPage.url(),
});

defineOptions({
    layout: {
        breadcrumbs: [
            {
                translationKey: 'tools.nav.home',
                title: 'Home',
                href: dashboard.url(),
            },
            {
                translationKey: 'tools.pdf.sign.title',
                title: 'Sign PDF',
                href: signPage.url(),
            },
        ],
    },
});

type Tab = 'draw' | 'type' | 'upload';
type Position = 'bottom-left' | 'bottom-center' | 'bottom-right';
type Status = 'idle' | 'loading' | 'success' | 'error';

const file = ref<File | null>(null);
/** Page shown in preview and where the signature is applied */
const targetPage = ref(1);

const activeTab = ref<Tab>('draw');
const signatureColor = ref('#1a1a1a');

/** Bumps when signature pixels change so the PDF overlay redraws */
const sigSourceTick = ref(0);

// Draw tab
const drawCanvas = ref<HTMLCanvasElement | null>(null);
const isDrawing = ref(false);
let lastX = 0;
let lastY = 0;

// Type tab
const typeCanvas = ref<HTMLCanvasElement | null>(null);
const typedName = ref('');

// Upload tab
const uploadedFile = ref<File | null>(null);
const uploadedPreview = ref('');
const uploadedImg = ref<HTMLImageElement | null>(null);

const position = ref<Position>('bottom-right');
const signatureWidth = ref(25);

/** Normalized top-left placement on the page (0–1) */
const sigPlacement = ref({ x: 0.65, y: 0.88 });
const isDraggingSig = ref(false);
const sigDragOffset = ref({ x: 0, y: 0 });

function formatSigWidthPct(n: number): string {
    return `${n}%`;
}

const status = ref<Status>('idle');
const statusMessage = ref('');

const pdfCanvas = ref<HTMLCanvasElement | null>(null);
const overlayCanvas = ref<HTMLCanvasElement | null>(null);
const {
    isLoading: previewLoading,
    error: previewError,
    pageCount,
    pageWidthPt,
    pageHeightPt,
    previewZoomPct,
    loadFile,
    renderPage,
    unload,
} = usePdfPage();
const totalPreviewPages = computed(() => Math.max(1, pageCount.value));

const signTabs = computed(() => [
    { id: 'draw' as Tab, label: t('tools.pdf.ui.sign_tab_draw', 'Draw') },
    { id: 'type' as Tab, label: t('tools.pdf.ui.sign_tab_type', 'Type') },
    { id: 'upload' as Tab, label: t('tools.pdf.ui.sign_tab_upload', 'Upload') },
]);

const positionOptions = computed(() => [
    {
        label: t('tools.pdf.ui.sign_pos_bottom_left', 'Bottom left'),
        value: 'bottom-left' as Position,
    },
    {
        label: t('tools.pdf.ui.sign_pos_bottom_center', 'Bottom center'),
        value: 'bottom-center' as Position,
    },
    {
        label: t('tools.pdf.ui.sign_pos_bottom_right', 'Bottom right'),
        value: 'bottom-right' as Position,
    },
]);

const pagesDetectedLine = computed(() => {
    const n = pageCount.value;

    if (n <= 1) {
        return t(
            'tools.pdf.ui.sign_pages_one',
            '{n} page detected · signature is placed on the preview page',
        ).replace('{n}', String(n));
    }

    return t(
        'tools.pdf.ui.sign_pages_many',
        '{n} pages detected · signature is placed on the preview page',
    ).replace('{n}', String(n));
});

async function onFileSelected(files: File[]): Promise<void> {
    file.value = files[0] ?? null;
    status.value = 'idle';

    if (!file.value) {
        unload();

        return;
    }

    await loadFile(file.value);
    await nextTick();

    if (!pdfCanvas.value) {
        return;
    }

    const last = Math.max(1, pageCount.value);
    targetPage.value = last;
    await renderPreviewPage(last);
}

function removeFile(): void {
    file.value = null;
    status.value = 'idle';
    targetPage.value = 1;
    unload();
}

function sigSizeNorm(): { w: number; h: number } {
    const w = signatureWidth.value / 100;

    return { w, h: w * 0.4 };
}

function marginNorm(): { x: number; y: number } {
    const refW = pageWidthPt.value > 0 ? pageWidthPt.value : 595;
    const refH = pageHeightPt.value > 0 ? pageHeightPt.value : 842;

    return { x: 10 / refW, y: 10 / refH };
}

function clampSigPlacement(x: number, y: number): { x: number; y: number } {
    const { w, h } = sigSizeNorm();
    const m = marginNorm();

    return {
        x: Math.max(m.x, Math.min(1 - w - m.x, x)),
        y: Math.max(m.y, Math.min(1 - h - m.y, y)),
    };
}

function applyPositionPreset(pos: Position): void {
    const { w, h } = sigSizeNorm();
    const m = marginNorm();

    let x = 1 - w - m.x;
    const y = 1 - h - m.y;

    if (pos === 'bottom-left') {
        x = m.x;
    } else if (pos === 'bottom-center') {
        x = (1 - w) / 2;
    }

    sigPlacement.value = { x, y };
}

function getSigRectPx(
    pw: number,
    ph: number,
): {
    x: number;
    y: number;
    w: number;
    h: number;
} {
    const { w, h } = sigSizeNorm();

    return {
        x: sigPlacement.value.x * pw,
        y: sigPlacement.value.y * ph,
        w: pw * w,
        h: ph * h,
    };
}

function getOverlayPoint(event: MouseEvent | PointerEvent): {
    x: number;
    y: number;
} {
    const canvas = overlayCanvas.value!;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
        x: (event.clientX - rect.left) * scaleX,
        y: (event.clientY - rect.top) * scaleY,
    };
}

function hitTestSignature(pt: { x: number; y: number }): boolean {
    if (!pdfCanvas.value) {
        return false;
    }

    const pw = pdfCanvas.value.width;
    const ph = pdfCanvas.value.height;
    const r = getSigRectPx(pw, ph);

    return pt.x >= r.x && pt.x <= r.x + r.w && pt.y >= r.y && pt.y <= r.y + r.h;
}

function onSigOverlayPointerDown(event: PointerEvent): void {
    if (!file.value || previewLoading.value) {
        return;
    }

    const pt = getOverlayPoint(event);
    const pw = pdfCanvas.value!.width;
    const ph = pdfCanvas.value!.height;
    const r = getSigRectPx(pw, ph);

    if (!hitTestSignature(pt)) {
        return;
    }

    isDraggingSig.value = true;
    sigDragOffset.value = { x: pt.x - r.x, y: pt.y - r.y };
    overlayCanvas.value?.setPointerCapture(event.pointerId);
    event.preventDefault();
}

function onSigOverlayPointerMove(event: PointerEvent): void {
    if (!isDraggingSig.value || !pdfCanvas.value) {
        return;
    }

    const pt = getOverlayPoint(event);
    const pw = pdfCanvas.value.width;
    const ph = pdfCanvas.value.height;
    const xNorm = (pt.x - sigDragOffset.value.x) / pw;
    const yNorm = (pt.y - sigDragOffset.value.y) / ph;

    sigPlacement.value = clampSigPlacement(xNorm, yNorm);
    void drawSignatureOverlay();
}

function onSigOverlayPointerUp(event: PointerEvent): void {
    if (overlayCanvas.value?.hasPointerCapture(event.pointerId)) {
        overlayCanvas.value.releasePointerCapture(event.pointerId);
    }

    isDraggingSig.value = false;
}

async function renderPreviewPage(page: number): Promise<void> {
    if (!pdfCanvas.value) {
        return;
    }

    const clamped = Math.min(
        totalPreviewPages.value,
        Math.max(1, Math.floor(page)),
    );
    targetPage.value = clamped;
    await renderPage(pdfCanvas.value, clamped, 560);
    await nextTick();
    void drawSignatureOverlay();
}

async function drawSignatureOverlay(): Promise<void> {
    if (
        previewLoading.value ||
        !overlayCanvas.value ||
        !pdfCanvas.value ||
        !file.value ||
        !pageCount.value
    ) {
        return;
    }

    const pw = pdfCanvas.value.width;
    const ph = pdfCanvas.value.height;

    if (pw === 0 || ph === 0) {
        return;
    }

    overlayCanvas.value.width = pw;
    overlayCanvas.value.height = ph;
    const ctx = overlayCanvas.value.getContext('2d');

    if (!ctx) {
        return;
    }

    ctx.clearRect(0, 0, pw, ph);

    const dataUrl = getSignatureDataUrl();
    const img = new Image();

    await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('signature image'));
        img.src = dataUrl;
    }).catch(() => {
        return;
    });

    if (!img.complete || img.naturalWidth === 0) {
        return;
    }

    const { x, y, w: sigW, h: sigH } = getSigRectPx(pw, ph);

    ctx.drawImage(img, x, y, sigW, sigH);

    if (file.value) {
        ctx.strokeStyle = isDraggingSig.value
            ? '#4f46e5'
            : 'rgba(99, 102, 241, 0.7)';
        ctx.lineWidth = isDraggingSig.value ? 2.5 : 2;
        ctx.setLineDash(isDraggingSig.value ? [] : [5, 4]);
        ctx.strokeRect(x, y, sigW, sigH);
        ctx.setLineDash([]);
    }
}

watch(position, (pos) => {
    applyPositionPreset(pos);
});

watch(signatureWidth, () => {
    sigPlacement.value = clampSigPlacement(
        sigPlacement.value.x,
        sigPlacement.value.y,
    );
});

watch(previewZoomPct, () => {
    if (!file.value || !pdfCanvas.value) {
        return;
    }

    void renderPreviewPage(targetPage.value);
});

watchEffect(() => {
    void targetPage.value;
    void position.value;
    void sigPlacement.value;
    void signatureWidth.value;
    void isDraggingSig.value;
    void activeTab.value;
    void typedName.value;
    void uploadedPreview.value;
    void signatureColor.value;
    void sigSourceTick.value;
    void pageCount.value;
    void previewLoading.value;
    void pdfCanvas.value;
    void overlayCanvas.value;

    if (previewLoading.value || !pdfCanvas.value?.width) {
        return;
    }

    queueMicrotask(() => {
        void drawSignatureOverlay();
    });
});

// Draw canvas helpers
function initDrawCanvas(): void {
    if (!drawCanvas.value) {
        return;
    }

    const ctx = drawCanvas.value.getContext('2d')!;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, 400, 160);
    sigSourceTick.value++;
}

function startDraw(e: PointerEvent): void {
    isDrawing.value = true;
    const rect = drawCanvas.value!.getBoundingClientRect();
    const scaleX = 400 / rect.width;
    const scaleY = 160 / rect.height;
    lastX = (e.clientX - rect.left) * scaleX;
    lastY = (e.clientY - rect.top) * scaleY;
}

function onDraw(e: PointerEvent): void {
    if (!isDrawing.value) {
        return;
    }

    const ctx = drawCanvas.value!.getContext('2d')!;
    const rect = drawCanvas.value!.getBoundingClientRect();
    const scaleX = 400 / rect.width;
    const scaleY = 160 / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    ctx.beginPath();
    ctx.moveTo(lastX, lastY);
    ctx.lineTo(x, y);
    ctx.strokeStyle = signatureColor.value;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
    lastX = x;
    lastY = y;
}

function stopDraw(): void {
    if (isDrawing.value) {
        sigSourceTick.value++;
    }

    isDrawing.value = false;
}

function clearDraw(): void {
    initDrawCanvas();
}

// Type tab helpers
function renderTypeCanvas(): void {
    if (!typeCanvas.value) {
        return;
    }

    const ctx = typeCanvas.value.getContext('2d')!;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, 400, 160);

    if (typedName.value) {
        ctx.font = '52px "Brush Script MT", cursive';
        ctx.fillStyle = signatureColor.value;
        ctx.fillText(typedName.value, 20, 110);
    }

    sigSourceTick.value++;
}

watch(typedName, renderTypeCanvas);
watch(signatureColor, () => {
    renderTypeCanvas();
});

watch(activeTab, (tab) => {
    void nextTick(() => {
        if (tab === 'type') {
            renderTypeCanvas();
        } else {
            sigSourceTick.value++;
        }
    });
});

// Upload tab helpers
function onUploadChange(e: Event): void {
    const input = e.target as HTMLInputElement;
    const f = input.files?.[0];

    if (!f) {
        return;
    }

    uploadedFile.value = f;
    const reader = new FileReader();
    reader.onload = (ev) => {
        uploadedPreview.value = ev.target?.result as string;
        const img = new Image();
        img.onload = () => {
            uploadedImg.value = img;
            sigSourceTick.value++;
        };
        img.src = uploadedPreview.value;
    };
    reader.readAsDataURL(f);
}

function getSignatureDataUrl(): string {
    if (activeTab.value === 'draw') {
        return drawCanvas.value!.toDataURL('image/png');
    }

    if (activeTab.value === 'type') {
        return typeCanvas.value!.toDataURL('image/png');
    }

    const c = document.createElement('canvas');
    c.width = 400;
    c.height = 160;
    const ctx = c.getContext('2d')!;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, 400, 160);

    if (uploadedImg.value) {
        ctx.drawImage(uploadedImg.value, 0, 0, 400, 160);
    }

    return c.toDataURL('image/png');
}

async function extractError(err: unknown): Promise<string> {
    const e = err as { response?: { data: Blob | object } };

    if (e.response?.data instanceof Blob) {
        try {
            const parsed = JSON.parse(await (e.response.data as Blob).text());
            const msgs = Object.values(parsed.errors ?? {}).flat();

            return (
                (msgs[0] as string) ||
                t('tools.pdf.ui.sign_failed', 'Sign failed.')
            );
        } catch {
            return t('tools.pdf.ui.sign_failed', 'Sign failed.');
        }
    }

    return t('tools.pdf.ui.sign_failed', 'Sign failed.');
}

async function sign(): Promise<void> {
    if (!file.value || status.value === 'loading') {
        return;
    }

    status.value = 'loading';
    statusMessage.value = t('tools.pdf.ui.sign_adding', 'Adding signature...');

    const formData = new FormData();
    formData.append('file', file.value);
    formData.append('signature', getSignatureDataUrl());
    formData.append('page', String(targetPage.value));
    formData.append('position', position.value);
    formData.append('sig_x', String(sigPlacement.value.x));
    formData.append('sig_y', String(sigPlacement.value.y));
    formData.append('signature_width', String(signatureWidth.value));

    try {
        const response = await axios.post(signTool.submit.url(), formData, {
            responseType: 'blob',
        });

        const cd = (response.headers['content-disposition'] as string) ?? '';
        const match = cd.match(/filename="?([^"]+)"?/);
        const filename = match?.[1] ?? 'signed.pdf';

        const blobUrl = URL.createObjectURL(response.data as Blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(blobUrl);

        status.value = 'success';
        statusMessage.value = t(
            'tools.pdf.ui.sign_success',
            'PDF signed successfully!',
        );
        file.value = null;
        targetPage.value = 1;
        unload();
    } catch (err: unknown) {
        status.value = 'error';
        statusMessage.value = await extractError(err);
    }
}

onMounted(() => {
    initDrawCanvas();
    applyPositionPreset(position.value);
    const auth = inertiaPage.props.auth as
        | { user?: { name?: string } }
        | undefined;

    if (auth?.user?.name) {
        typedName.value = auth.user.name;
    }

    renderTypeCanvas();
});

onUnmounted(unload);
</script>

<template>
    <ToolPageHead v-bind="seo" json-ld-head-key="ld-json-pdf-sign" />

    <ToolLayout
        :title="t('tools.pdf.sign.title', 'Sign PDF')"
        :subtitle="t('tools.pdf.sign.description', '')"
        :show-how-to="false"
    >
        <div class="grid gap-4 lg:grid-cols-[3fr_2fr]">
            <!-- ── Left: PDF preview ── -->
            <div class="lg:sticky lg:top-4 lg:self-start">
                <PdfLivePreview
                    v-model:zoom="previewZoomPct"
                    :has-file="!!file"
                    :loading="previewLoading"
                    :error="previewError"
                    :current-page="targetPage"
                    :total-pages="pageCount"
                    :rendering-message="
                        t('tools.pdf.ui.rendering', 'Rendering…')
                    "
                    @change-page="renderPreviewPage"
                >
                    <div
                        class="flex min-h-48 items-center justify-center px-2 py-4"
                    >
                        <div class="relative inline-block max-w-full">
                            <canvas
                                ref="pdfCanvas"
                                class="relative z-0 block h-auto max-h-none rounded-md bg-white shadow-md dark:bg-gray-950"
                            />
                            <canvas
                                ref="overlayCanvas"
                                class="absolute top-0 left-0 z-10 max-w-full"
                                :class="
                                    file && !previewLoading
                                        ? isDraggingSig
                                            ? 'cursor-grabbing'
                                            : 'cursor-grab'
                                        : 'pointer-events-none'
                                "
                                style="touch-action: none"
                                @pointerdown="onSigOverlayPointerDown"
                                @pointermove="onSigOverlayPointerMove"
                                @pointerup="onSigOverlayPointerUp"
                                @pointercancel="onSigOverlayPointerUp"
                            />
                        </div>
                    </div>
                    <template #footer>
                        <p
                            v-if="file && !previewLoading"
                            class="mt-3 text-center text-xs text-gray-400 dark:text-gray-500"
                        >
                            {{
                                t(
                                    'tools.pdf.ui.sign_preview_footer_before',
                                    'Preview shows page',
                                )
                            }}
                            {{ targetPage }}
                            {{
                                t(
                                    'tools.pdf.ui.sign_preview_footer_after',
                                    'with your signature overlaid. Drag it on the preview to move.',
                                )
                            }}
                        </p>
                    </template>
                </PdfLivePreview>
                <div class="mt-4">
                    <ToolHowToCard />
                </div>
            </div>

            <!-- ── Right: controls ── -->
            <div class="space-y-4">
                <div
                    class="rounded-xl border border-border bg-card p-5 shadow-sm"
                >
                    <DropZone
                        accept="application/pdf"
                        :multiple="false"
                        :label="
                            t(
                                'tools.pdf.ui.drop_pdf',
                                'Click or drag a PDF here',
                            )
                        "
                        :sublabel="
                            lineWithMax(
                                t('tools.pdf.ui.one_pdf_file', 'One PDF file'),
                            )
                        "
                        @update:model-value="onFileSelected"
                    />
                    <div v-if="file" class="mt-3">
                        <FileCard
                            :file="file"
                            :show-drag-handle="false"
                            @remove="removeFile"
                        />
                    </div>
                    <p
                        v-if="file && pageCount > 0 && !previewLoading"
                        class="mt-2 text-xs text-gray-500 dark:text-gray-400"
                    >
                        {{ pagesDetectedLine }}
                    </p>
                </div>

                <!-- Signature creation -->
                <div
                    class="rounded-xl border border-border bg-card p-5 shadow-sm"
                >
                    <div class="mb-4 flex items-center justify-between">
                        <p
                            class="text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                            {{
                                t(
                                    'tools.pdf.ui.sign_signature_heading',
                                    'Signature',
                                )
                            }}
                        </p>
                        <div class="flex items-center gap-2">
                            <label class="text-xs text-gray-500">{{
                                t('tools.pdf.ui.sign_color', 'Color')
                            }}</label>
                            <input
                                v-model="signatureColor"
                                type="color"
                                class="h-7 w-10 cursor-pointer rounded border border-gray-200 p-0.5 dark:border-gray-700"
                            />
                        </div>
                    </div>

                    <!-- Tabs -->
                    <div
                        class="mb-4 flex rounded-lg border border-gray-200 p-1 dark:border-gray-700"
                    >
                        <button
                            v-for="tab in signTabs"
                            :key="tab.id"
                            type="button"
                            class="flex-1 rounded-md py-1.5 text-sm font-medium transition-colors"
                            :class="
                                activeTab === tab.id
                                    ? 'bg-brand-600 text-white'
                                    : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'
                            "
                            @click="activeTab = tab.id"
                        >
                            {{ tab.label }}
                        </button>
                    </div>

                    <!-- Draw tab -->
                    <div v-if="activeTab === 'draw'">
                        <canvas
                            ref="drawCanvas"
                            width="400"
                            height="160"
                            class="w-full rounded-lg border border-gray-200 bg-white dark:border-gray-700"
                            style="touch-action: none; cursor: crosshair"
                            @pointerdown="startDraw"
                            @pointermove="onDraw"
                            @pointerup="stopDraw"
                            @pointerleave="stopDraw"
                        />
                        <button
                            type="button"
                            class="mt-2 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            @click="clearDraw"
                        >
                            {{ t('tools.pdf.ui.sign_clear', 'Clear') }}
                        </button>
                    </div>

                    <!-- Type tab -->
                    <div v-if="activeTab === 'type'">
                        <input
                            v-model="typedName"
                            type="text"
                            :placeholder="
                                t(
                                    'tools.pdf.ui.type_your_name',
                                    'Type your name',
                                )
                            "
                            class="mb-3 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-500 focus:outline-none dark:border-border dark:bg-muted dark:text-foreground"
                        />
                        <canvas
                            ref="typeCanvas"
                            width="400"
                            height="160"
                            class="w-full rounded-lg border border-gray-200 bg-white dark:border-gray-700"
                        />
                    </div>

                    <!-- Upload tab -->
                    <div v-if="activeTab === 'upload'">
                        <label
                            class="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-200 p-6 text-center hover:border-brand-300 dark:border-gray-700"
                        >
                            <span
                                class="text-sm text-gray-500 dark:text-gray-400"
                                >{{
                                    t(
                                        'tools.pdf.ui.sign_upload_title',
                                        'Click to upload an image',
                                    )
                                }}</span
                            >
                            <span class="mt-1 text-xs text-gray-400">{{
                                t(
                                    'tools.pdf.ui.sign_upload_hint',
                                    'PNG, JPG, SVG',
                                )
                            }}</span>
                            <input
                                type="file"
                                accept="image/*"
                                class="hidden"
                                @change="onUploadChange"
                            />
                        </label>
                        <img
                            v-if="uploadedPreview"
                            :src="uploadedPreview"
                            :alt="
                                t(
                                    'tools.pdf.ui.sign_upload_alt',
                                    'Signature preview',
                                )
                            "
                            class="mt-3 max-h-32 rounded-lg border border-gray-200 object-contain dark:border-gray-700"
                        />
                    </div>
                </div>

                <!-- Placement options -->
                <div
                    class="rounded-xl border border-border bg-card p-5 shadow-sm"
                >
                    <div class="space-y-4">
                        <p
                            v-if="pageCount > 0"
                            class="text-xs text-gray-500 dark:text-gray-400"
                        >
                            {{
                                t(
                                    'tools.pdf.ui.sign_pick_page_hint',
                                    'Use the preview slider or page controls to pick the page where the signature is applied.',
                                )
                            }}
                        </p>

                        <div>
                            <p
                                class="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300"
                            >
                                {{
                                    t('tools.pdf.ui.sign_position', 'Position')
                                }}
                            </p>
                            <div class="grid grid-cols-3 gap-2">
                                <button
                                    v-for="opt in positionOptions"
                                    :key="opt.value"
                                    type="button"
                                    class="rounded-lg border px-3 py-2 text-sm font-medium transition-colors"
                                    :class="
                                        position === opt.value
                                            ? 'border-brand-600 bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300'
                                            : 'border-border bg-card text-muted-foreground hover:border-brand-300 dark:text-foreground/85'
                                    "
                                    @click="
                                        position = opt.value;
                                        applyPositionPreset(opt.value);
                                    "
                                >
                                    {{ opt.label }}
                                </button>
                            </div>
                            <p class="mt-2 text-xs text-muted-foreground">
                                {{
                                    t(
                                        'tools.pdf.ui.sign_drag_hint',
                                        'Or drag the signature directly on the preview.',
                                    )
                                }}
                            </p>
                        </div>

                        <ToolSliderField
                            v-model="signatureWidth"
                            :label="
                                t(
                                    'tools.pdf.ui.signature_width',
                                    'Signature width',
                                )
                            "
                            :min="10"
                            :max="80"
                            :format-value="formatSigWidthPct"
                        />
                        <p class="mt-1 text-xs text-muted-foreground">
                            {{
                                t(
                                    'tools.pdf.ui.pct_of_page_width',
                                    '% of page width',
                                )
                            }}
                        </p>
                    </div>
                </div>

                <Button
                    class="w-full bg-brand-600 text-white hover:bg-brand-700"
                    :disabled="!file || status === 'loading'"
                    @click="sign"
                >
                    {{ t('tools.pdf.ui.sign_button', 'Sign PDF') }}
                </Button>

                <StatusMessage :status="status" :message="statusMessage" />
            </div>
        </div>
    </ToolLayout>
</template>
